import { Check, X } from 'lucide-react';
import type { TakerQuestion } from '@ready/shared';

/**
 * Answer options for a question, shared by the quiz runner and lesson
 * knowledge checks. Feedback marks only the learner's own choice; the right
 * option is never revealed.
 */
export function AnswerOptions({
  question,
  chosen,
  feedback,
  onToggle,
}: {
  question: TakerQuestion;
  chosen: string[];
  /** Set once the answer is checked: marks the learner's own choice right or wrong. */
  feedback?: { correct: boolean };
  onToggle: (id: string) => void;
}) {
  return (
    <div role={question.type === 'multiple' ? 'group' : 'radiogroup'} className="mt-4 space-y-2">
      {question.options.map((o) => {
        const selected = chosen.includes(o.id);
        // Feedback marks only the learner's own choice; the right option is never revealed.
        const state =
          feedback && selected
            ? feedback.correct
              ? 'right'
              : 'wrong'
            : selected
              ? 'selected'
              : 'idle';
        const multiple = question.type === 'multiple';
        return (
          <button
            key={o.id}
            type="button"
            role={multiple ? 'checkbox' : 'radio'}
            aria-checked={selected}
            disabled={Boolean(feedback)}
            onClick={() => onToggle(o.id)}
            className={`flex w-full items-center gap-3 rounded-lg border-2 px-4 py-3 text-left text-[15px] leading-snug transition-colors ${
              state === 'selected'
                ? 'border-primary bg-primary/10'
                : state === 'right'
                  ? 'border-india-green bg-india-green/15'
                  : state === 'wrong'
                    ? 'border-destructive bg-destructive/15'
                    : 'border-border hover:bg-muted'
            } ${feedback && !selected ? 'opacity-60' : ''}`}
          >
            <OptionMark multiple={multiple} state={state} />
            <span className="min-w-0 flex-1">{o.text}</span>
            {(state === 'right' || state === 'wrong') && (
              <span className="sr-only">Your answer</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** The radio circle or checkbox square at the start of an option, showing its state. */
function OptionMark({
  multiple,
  state,
}: {
  multiple: boolean;
  state: 'idle' | 'selected' | 'right' | 'wrong';
}) {
  const shape = multiple ? 'rounded-md' : 'rounded-full';
  if (state === 'right')
    return (
      <span
        className={`flex size-6 shrink-0 items-center justify-center bg-india-green text-white ${shape}`}
      >
        <Check className="size-4" strokeWidth={3} aria-hidden />
      </span>
    );
  if (state === 'wrong')
    return (
      <span
        className={`flex size-6 shrink-0 items-center justify-center bg-destructive text-white ${shape}`}
      >
        <X className="size-4" strokeWidth={3} aria-hidden />
      </span>
    );
  if (state === 'selected')
    return (
      <span
        className={`flex size-6 shrink-0 items-center justify-center border-2 border-primary bg-primary text-primary-foreground ${shape}`}
      >
        {multiple ? (
          <Check className="size-4" strokeWidth={3} aria-hidden />
        ) : (
          <span className="size-2.5 rounded-full bg-primary-foreground" />
        )}
      </span>
    );
  return (
    <span className={`size-6 shrink-0 border-2 border-muted-foreground/50 ${shape}`} aria-hidden />
  );
}
